// components/process-units/Dewpoint.js
import React, { useState } from 'react';

const Dewpoint = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Dewpoint Control / NGL Recovery</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Dewpoint control removes heavier hydrocarbons (C2+ components) from natural gas to meet
            pipeline hydrocarbon dewpoint specifications and recover valuable natural gas liquids (NGLs).
            Common technologies include refrigeration, turboexpansion, or JT expansion.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Process Type</label>
              <select 
                value={config.processType || 'refrigeration'}
                onChange={(e) => updateConfig({ processType: e.target.value })}
              >
                <option value="refrigeration">Mechanical Refrigeration</option>
                <option value="turboExpander">Turboexpander</option>
                <option value="jtExpansion">Joule-Thomson Expansion</option>
                <option value="deepcut">Deepcut Recovery</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Hydrocarbon Dewpoint Spec (°C)</label>
              <input
                type="number"
                min="-40"
                max="10"
                step="1"
                value={config.dewpointSpec || -10}
                onChange={(e) => handleInputChange('dewpointSpec', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Ethane Recovery (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={config.ethaneRecovery || 80}
                onChange={(e) => handleInputChange('ethaneRecovery', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Propane Recovery (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={config.propaneRecovery || 95}
                onChange={(e) => handleInputChange('propaneRecovery', e.target.value)}
              />
            </div>
            
            {config.processType === 'refrigeration' && (
              <>
                <div className="parameter-row">
                  <label>Refrigerant Type</label>
                  <select 
                    value={config.refrigerantType || 'propane'}
                    onChange={(e) => updateConfig({ refrigerantType: e.target.value })}
                  >
                    <option value="propane">Propane</option>
                    <option value="ethane">Ethane</option>
                    <option value="mixed">Mixed Refrigerant</option>
                    <option value="ammonia">Ammonia</option>
                  </select>
                </div>
                
                <div className="parameter-row">
                  <label>Refrigeration Temperature (°C)</label>
                  <input
                    type="number"
                    min="-100"
                    max="0"
                    step="1"
                    value={config.refrigerationTemp || -35}
                    onChange={(e) => handleInputChange('refrigerationTemp', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Refrigeration Compressor Efficiency (%)</label>
                  <input
                    type="number"
                    min="50"
                    max="90"
                    step="1"
                    value={(config.compressorEfficiency || 0.75) * 100}
                    onChange={(e) => handleInputChange('compressorEfficiency', e.target.value / 100)}
                  />
                </div>
              </>
            )}
            
            {config.processType === 'turboExpander' && (
              <>
                <div className="parameter-row">
                  <label>Expander Efficiency (%)</label>
                  <input
                    type="number"
                    min="50"
                    max="90"
                    step="1"
                    value={(config.expanderEfficiency || 0.80) * 100}
                    onChange={(e) => handleInputChange('expanderEfficiency', e.target.value / 100)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Expander Outlet Temperature (°C)</label>
                  <input
                    type="number"
                    min="-120"
                    max="0"
                    step="1"
                    value={config.expanderOutletTemp || -85}
                    onChange={(e) => handleInputChange('expanderOutletTemp', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Recompression Ratio</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    step="0.1"
                    value={config.recompressionRatio || 3}
                    onChange={(e) => handleInputChange('recompressionRatio', e.target.value)}
                  />
                </div>
              </>
            )}
            
            {config.processType === 'jtExpansion' && (
              <>
                <div className="parameter-row">
                  <label>Inlet Pressure (bar)</label>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    step="1"
                    value={config.jtInletPressure || 80}
                    onChange={(e) => handleInputChange('jtInletPressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Outlet Pressure (bar)</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    step="1"
                    value={config.jtOutletPressure || 30}
                    onChange={(e) => handleInputChange('jtOutletPressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Pre-cooling Temperature (°C)</label>
                  <input
                    type="number"
                    min="-50"
                    max="40"
                    step="1"
                    value={config.precoolingTemp || 15}
                    onChange={(e) => handleInputChange('precoolingTemp', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>NGL Product Specification</label>
              <select 
                value={config.nglSpec || 'rawMix'}
                onChange={(e) => updateConfig({ nglSpec: e.target.value })}
              >
                <option value="rawMix">Raw NGL Mix</option>
                <option value="ethanePlus">Ethane Plus (C2+)</option>
                <option value="propanePlus">Propane Plus (C3+)</option>
                <option value="butanePlus">Butane Plus (C4+)</option>
              </select>
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>Mechanical refrigeration has higher energy consumption but lower capital cost and complexity.</li>
              <li>Turboexpanders recover work during expansion, reducing energy requirements but adding complexity.</li>
              <li>JT expansion is simple but less efficient, requiring higher inlet pressure or pre-cooling.</li>
              <li>Deepcut facilities recover more ethane but require significantly more energy.</li>
              <li>Energy consumption increases exponentially as the target temperature decreases.</li>
              <li>Heat exchanger approach temperatures significantly impact overall efficiency.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dewpoint;